/**
 * VerificationInput Component
 *
 * Inline verification code input with enter/exit animations.
 * Displays a 6-digit code field with resend button.
 *
 * @module RecipientItem/VerificationInput
 */

import {
  Box,
  Icon,
  InlineStack,
  Spinner,
  TextField,
  Tooltip,
  Button,
} from '@shopify/polaris';
import { RefreshIcon, XCircleIcon } from '@shopify/polaris-icons';

import type { VerificationState } from './types';
import { VERIFICATION_CODE_LENGTH, ARIA_LABELS } from './constants';

export interface VerificationInputProps {
  /** Current verification code value */
  code: string;
  /** Current verification state */
  verificationState: VerificationState;
  /** Whether the input is visible */
  isVisible: boolean;
  /** Whether the input is animating out */
  isExiting: boolean;
  /** Callback when code changes */
  onCodeChange: (value: string) => void;
  /** Callback when Enter key is pressed */
  onSubmit: () => void;
  /** Callback when resend button is clicked */
  onResend: () => void;
}

/**
 * Inline verification code input component
 *
 * Displays a 6-digit code field with:
 * - Error indicator when code is wrong
 * - Spinner when verifying
 * - Resend button
 * - Enter/exit animations
 */
export function VerificationInput({
  code,
  verificationState,
  isVisible,
  isExiting,
  onCodeChange,
  onSubmit,
  onResend,
}: VerificationInputProps) {
  if (!isVisible) {
    return null;
  }

  const isError = verificationState === 'error';
  const isVerifying = verificationState === 'verifying';

  // Build animation class
  const animationClass = isExiting
    ? 'verification-code-input-exit'
    : 'verification-code-input-enter';

  // Handle keydown for Enter submission
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isVerifying && code.length === VERIFICATION_CODE_LENGTH) {
      event.preventDefault();
      onSubmit();
    }
  };

  // Handle code change with validation
  const handleChange = (value: string) => {
    // Only allow digits
    const digitsOnly = value.replace(/\D/g, '');
    onCodeChange(digitsOnly);
  };

  return (
    <div className={`verification-code-input-wrapper ${animationClass}`}>
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <div
          className="verification-code-field"
          onKeyDown={handleKeyDown}
        >
          <TextField
            label="Verification code"
            labelHidden
            value={code}
            onChange={handleChange}
            placeholder="6-digit code"
            autoComplete="one-time-code"
            maxLength={VERIFICATION_CODE_LENGTH}
            disabled={isVerifying}
            error={isError}
            connectedRight={
              isError ? (
                <Tooltip content="Wrong code">
                  <Box paddingInline="200" color="text-critical">
                    <Icon source={XCircleIcon} tone="critical" />
                  </Box>
                </Tooltip>
              ) : isVerifying ? (
                <Box paddingInline="200">
                  <Spinner size="small" />
                </Box>
              ) : null
            }
          />
        </div>

        <Tooltip content="Resend code">
          <Button
            size="slim"
            variant="tertiary"
            onClick={onResend}
            icon={RefreshIcon}
            disabled={isVerifying}
            accessibilityLabel={ARIA_LABELS.RESEND_CODE}
          />
        </Tooltip>
      </InlineStack>
    </div>
  );
}
